import { format, parseISO, isValid } from "date-fns";
import { nn } from "date-fns/locale";

export const WEEKDAYS = ["Måndag", "Tysdag", "Onsdag", "Torsdag", "Fredag", "Laurdag", "Sundag"];
export const WEEKDAYS_SHORT = ["Mån", "Tys", "Ons", "Tor", "Fre", "Lau", "Sun"];

export const toISODate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const todayISO = () => toISODate(new Date());

export const fmtDate = (iso, pattern = "d. MMMM yyyy") => {
  if (!iso) return "";
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  return format(d, pattern, { locale: nn });
};

export const fmtDateShort = (iso) => fmtDate(iso, "d. MMM");
export const fmtDateTime = (iso) => fmtDate(iso, "d. MMM yyyy 'kl.' HH:mm");

export const fmtRange = (start, end) => {
  if (!start) return "";
  if (!end || end === start) return fmtDate(start);
  const s = parseISO(start);
  const e = parseISO(end);
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${format(s, "d.", { locale: nn })}–${format(e, "d. MMMM yyyy", { locale: nn })}`;
  }
  return `${fmtDate(start, "d. MMM")} – ${fmtDate(end)}`;
};

export const fmtTime = (start, end) => {
  if (!start && !end) return "";
  if (start && end) return `kl. ${start}–${end}`;
  return `kl. ${start || end}`;
};

export const fmtPrice = (n) => {
  if (n === null || n === undefined || n === "") return "–";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return `${num.toLocaleString("nb-NO").replace(/\u00a0/g, " ")},-`;
};

/** Lowest "real" rental rate: skips hourly add-ons, supplements and member-only lines so "frå"-prices stay honest. */
export const minPrice = (room) => {
  // Mirrors backend pricing.price_band(): only *base* rates count. Regular-user rates, add-ons,
  // hourly lines and closed lines never become "frå …" on the public site.
  const skipUnit = /^(pr time|tillegg|stengt)$/i;
  const skipLabel = /faste brukarar|faste for kor|^tillegg/i;
  const prices = (room?.price_lines || [])
    .filter((p) => (p.kind ? p.kind === "base" : !skipUnit.test((p.unit || "").trim()) && !skipLabel.test(p.label || "")))
    .map((p) => p.price)
    .filter((p) => typeof p === "number" && p > 0);
  if (!prices.length) return null;
  return Math.min(...prices);
};

export const monthLabel = (date) => format(date, "MMMM yyyy", { locale: nn });

export const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const paragraphs = (text) => (text || "").split(/\n\s*\n/).filter(Boolean);

export const floorLabel = (floor) => (floor ? `${floor}. etasje` : "Heile huset");

export const STATUS_LABEL = { ny: "Ny", godkjend: "Godkjend", avvist: "Avvist" };

export const EVENT_TYPES = [
  "Møte",
  "Konfirmasjon",
  "Bryllaup",
  "Dåp",
  "Gravferd / minnestund",
  "Fødselsdag / jubileum",
  "Konsert / arrangement",
  "Kurs / seminar",
  "Øving",
  "Utstilling / messe",
  "Anna",
];

/** «I dag», «I morgon», «Om 5 dagar», «For 3 dagar sidan» – for upcoming/past ISO dates. */
export const relativeDayLabel = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((target - today) / 86_400_000);
  if (diff === 0) return "I dag";
  if (diff === 1) return "I morgon";
  if (diff === -1) return "I går";
  if (diff > 1 && diff < 7) return `Om ${diff} dagar`;
  if (diff >= 7 && diff < 14) return "Om ei veke";
  if (diff >= 14 && diff < 60) return `Om ${Math.round(diff / 7)} veker`;
  if (diff >= 60) return `Om ${Math.round(diff / 30)} månader`;
  if (diff < -1 && diff > -7) return `For ${-diff} dagar sidan`;
  if (diff <= -7 && diff > -60) return `For ${Math.round(-diff / 7)} veker sidan`;
  return `For ${Math.round(-diff / 30)} månader sidan`;
};
